/**
 * Safe sessionStorage parsers for the short-lived MFA state.
 *
 * The challenge / setup screens stash `{ challenge_token | setup_token,
 * expires_at, methods? }` in sessionStorage between the password verify
 * and the MFA verify step. The store is only ever populated by our own
 * `useAuth.loginAndRedirect` code, but a tampered or stale entry
 * (different browser session, malformed JSON, expired payload) MUST
 * not crash the view — the previous code blindly called `JSON.parse`
 * and rendered a blank route on a bad payload. These helpers return
 * `null` for anything that doesn't pass a shape + expiry check and
 * clear the offending key so the next read starts fresh.
 *
 * Note: a tampered payload cannot bypass auth. The challenge_token /
 * setup_token are signed and rate-limited server-side, and the backend
 * MFA procedures re-validate them on every call. The reason we
 * validate here is purely UX: a malformed token surfaces as "sign in
 * again" instead of a white screen.
 */

import { createForcedPasswordChangeStorage } from '@monorepo/composables';

const CHALLENGE_KEY = 'hub_mfa_challenge';
const SETUP_KEY = 'hub_mfa_setup';
// Forced password change (PCI DSS 8.3.5, #1198). Same shape and the same
// reasoning as the two above: a short-lived server-issued token carried across
// the two halves of a login, worthless without the server's validation. The
// implementation is shared with admin so the two apps cannot disagree about
// what a stale or expired entry means.
const forcedPasswordChange = createForcedPasswordChangeStorage('hub_forced_password_change');

function readShaped(key, validate) {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_err) {
    sessionStorage.removeItem(key);
    return null;
  }

  if (!validate(parsed)) {
    sessionStorage.removeItem(key);
    return null;
  }

  if (typeof parsed.expires_at === 'number' && Date.now() > parsed.expires_at) {
    sessionStorage.removeItem(key);
    return { expired: true };
  }

  return parsed;
}

export function readMfaChallenge() {
  return readShaped(
    CHALLENGE_KEY,
    (v) =>
      v &&
      typeof v === 'object' &&
      typeof v.challenge_token === 'string' &&
      v.challenge_token.length > 0 &&
      typeof v.expires_at === 'number'
  );
}

export function clearMfaChallenge() {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(CHALLENGE_KEY);
  }
}

export function writeMfaChallenge(payload) {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(CHALLENGE_KEY, JSON.stringify(payload));
  }
}

export function readMfaSetup() {
  return readShaped(
    SETUP_KEY,
    (v) =>
      v &&
      typeof v === 'object' &&
      typeof v.setup_token === 'string' &&
      v.setup_token.length > 0 &&
      typeof v.expires_at === 'number'
  );
}

export function clearMfaSetup() {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(SETUP_KEY);
  }
}

export function writeMfaSetup(payload) {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(SETUP_KEY, JSON.stringify(payload));
  }
}

export function readForcedPasswordChange() {
  return forcedPasswordChange.read();
}

export function clearForcedPasswordChange() {
  forcedPasswordChange.clear();
}

export function writeForcedPasswordChange(payload) {
  forcedPasswordChange.write(payload);
}
